import moment from "moment";
import { Injectable } from "@angular/core";

/**
 * @class LocalStorage
 * @description For more information: https://developer.mozilla.org/en-US/docs/Web/API/Storage
 */
@Injectable()
export class LocalStorage {
    /**
     * @method set
     * @description adds an item to the localstorage, types and dates are noted.
     *              PLEASE NOTE: arrays will be returned as a collection of strings regardless, so make
     *              objects your bff to retain previous types or be prepared.
     * @param key {string}
     * @param value {string|object}
     * @param dateStored {string}
     */
    set(key: string, value: any, dateStored?: string): void {
        if (value === null) {
            // if a value is null, local storage equates this as a remove/delete
            window.localStorage.removeItem(key);
        } else if (value !== undefined) {
            let type = "none";
            if (dateStored === undefined) {
                dateStored = moment()
                    .valueOf()
                    .toString();
            } else {
                dateStored = moment(dateStored)
                    .valueOf()
                    .toString();
            }
            switch (value.constructor) {
                case Array:
                    // Check if values of array are objects if so change object to stringified objects
                    if (value[0].constructor === Object) {
                        value = JSON.stringify(value);
                        type = "object";
                    } else {
                        value = value.toString();
                        type = "array";
                    }
                    break;
                case Number:
                    value = value.toString();
                    type = "number";
                    break;
                case Object: // aka JSON
                    value = JSON.stringify(value);
                    type = "object";
                    break;
                case String:
                    type = "string";
                    break;
                default:
                    break;
            }

            const data = type + "*&&*" + dateStored + "*&&*" + value;
            window.localStorage.setItem(key, data);
        }
    }

    /**
     * @method get
     * @description retrieves items from localstorage
     * @param key {string}
     * @param date {boolean}
     * @returns {object|string|null}
     */
    get(key: string, date?: boolean): any {
        let result: any;
        let data: any = window.localStorage.getItem(key);

        if (data == null) {
            return null;
        } // key hasn't been set, most likely interceptor caused this

        data = data.split("*&&*"); // turn string back to array [type, dateStored, value]

        switch (data[0]) {
            case "array": // Don't do this if you have to, JSON should be the defacto to retain type
                result = data[2].split(",");
                break;
            case "number":
                result = Number(data[2]);
                break;
            case "object": // aka JSON
                result = JSON.parse(data[2]);
                break;
            case "string":
                result = data[2];
                break;
            default:
                result = data[2];
                break;
        }

        if (date !== undefined) {
            // return array with date
            result = [data[1], result];
        }

        return result;
    }

    /**
     * @method remove
     * @description deletes the item specified by the key from localstorage
     * @param key
     */
    remove(key: string): void {
        window.localStorage.removeItem(key);
    }

    /**
     * @method clear
     * @description clears out the entire localstorage
     */
    clear(): void {
        window.localStorage.clear();
    }
}
